import { createContext } from 'react';
import { useMatch } from 'react-router-dom';

import { format } from 'date-fns';

import { SelectOption } from '@components/selectmulti';
import { TranslationMap, getScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';
import { getKeys } from '@utils/objects';
import { formatTime } from '@utils/strings';

import {
  AttributeFieldType,
  AttributeType,
  TravellerAttributeTemplate,
} from 'api/attributes/requests';
import { TravellerActionGroupAction } from 'api/traveller-actions/requests';
import {
  JourneyAction,
  JourneyActionValuesTypes,
  TravellerJourneyAction,
  TravellerJourneyListAction,
  TravellerJourneyListItemAction,
} from 'api/types';
import { useCachedWorkspaces } from 'api/workspaces/hooks';
import { Workspace } from 'api/workspaces/requests';
import { getWaitForTimeLength } from 'ui/actions/duration';
import { newDateIgnoreLocalTimezone } from 'ui/actions/timezone';

export const TestIdContext = createContext({} as Record<JourneyAction['shortId'], string>);

export function getWaitFixedDescriptionText(
  values: JourneyActionValuesTypes['wait_until_fixed_date']
) {
  const { t } = getScopedTranslate(nodeLocale);
  if (!values?.date) {
    return t('descriptionFixed', {
      date: '<Selected date>',
      time: '<Selected time>',
    });
  }

  const date = newDateIgnoreLocalTimezone(values.date.value);
  const hour = date.getHours();
  const minute = date.getMinutes();

  return t('descriptionFixed', {
    date: abbreviateDate(date, 'en', 'date-short-fancy-no-year'),
    time: `${hour}:${minute < 10 ? '0' + minute : minute}`,
  });
}

export function getWaitRelativeDescriptionText(
  values: JourneyActionValuesTypes['wait_until_relative_date'],
  locale: TranslationMap
): string {
  const { t } = getScopedTranslate(locale);
  const comparator = values?.comparator;
  const variables = values?.referenceDate?.variables || {};
  const referenceDate = variables[Object.keys(variables)[0]]?.label ?? undefined;
  const days = values?.days;
  const hour = values?.hour;
  const minute = values?.minute;
  const time = formatTime(hour?.value, minute?.value);
  if (!referenceDate || !hour || !minute) {
    return t('configureAction');
  } else if (comparator?.value === 'lt') {
    return t('descriptionBefore', {
      referenceDate: referenceDate,
      daysBefore: days?.value ?? '0',
      time,
    });
  } else if (comparator?.value === 'gt') {
    return t('descriptionAfter', {
      referenceDate: referenceDate,
      daysAfter: days?.value ?? '0',
      time,
    });
  } else {
    return t('descriptionExact', {
      referenceDate: referenceDate,
      time,
    });
  }
}

export function getWaitRelativeDescriptionJourneyText(
  values: JourneyActionValuesTypes['wait_until_relative_date']
): string {
  return getWaitRelativeDescriptionText(values, nodeLocale);
}

export const getWaitUntilDescription = (values: JourneyActionValuesTypes['wait_for_time']) => {
  const { t } = getScopedTranslate(nodeLocale);
  const timeLength = getWaitForTimeLength({
    days: values?.days?.value,
    hours: values?.hours?.value,
    minutes: values?.minutes?.value,
  });
  return timeLength ? t('description', { timeLength }) : t('configureAction');
};

export function getActionStatus(
  action:
    | TravellerJourneyAction
    | TravellerActionGroupAction
    | TravellerJourneyListAction
    | TravellerJourneyListItemAction
): 'completed' | 'in_progress' | 'not_started' | 'error' | 'skipped' {
  if (action.result?.dateSucceeded) {
    return 'completed';
  } else if (action.status === 'skipped') {
    return 'skipped';
  } else if (action.result?.dateStarted) {
    return 'in_progress';
  } else if (action.result?.dateFailed) {
    return 'error';
  }
  return 'not_started';
}

export function getActionRatio(
  action:
    | TravellerJourneyAction
    | TravellerActionGroupAction
    | TravellerJourneyListAction
    | TravellerJourneyListItemAction
): number {
  const status = getActionStatus(action);
  return status === 'completed' ? 1 : status === 'in_progress' ? 0.5 : 0;
}

export const companyTravellerAttributePrefix = 'company_traveller_attribute__';
export const journeyAttributePrefix = 'journey_attribute__';

const valueColumnIdSuffix = '-value';

const valueColumnLocale = {
  en: {
    valueSuffix: 'value',
    valuesSuffix: 'values',
    emailSuffix: 'email',
  },
  is: {
    valueSuffix: 'value',
    valuesSuffix: 'values',
    emailSuffix: 'email',
  },
};

const { t: translateSuffix } = getScopedTranslate(valueColumnLocale);

const getMultiSelectValues = (value: any) => {
  if (!Array.isArray(value)) {
    return '';
  }
  // Ordered like accessorMap.multi_select so the value and label columns match.
  const sortedOptions = [...value].sort((first, second) => {
    if (first.label === second.label) {
      return 0;
    }
    return first.label < second.label ? -1 : 1;
  });
  // An inline edit fills the cache with empty values until the index catches up.
  return getMultiOptionValuesArray(sortedOptions).filter(Boolean).join(';');
};

const valueColumnConfig: Partial<
  Record<
    AttributeFieldType,
    {
      suffixKey: keyof typeof valueColumnLocale.en;
      getText: (value: any) => string;
      isSortable?: boolean;
    }
  >
> = {
  single_select: { suffixKey: 'valueSuffix', getText: (value) => value?.value ?? '' },
  multi_select: {
    suffixKey: 'valuesSuffix',
    getText: getMultiSelectValues,
    isSortable: false,
  },
  user: { suffixKey: 'emailSuffix', getText: (value) => value?.email ?? '' },
};

export const hasValueColumn = (attributeType: string) => {
  return Boolean(valueColumnConfig[attributeType as AttributeFieldType]);
};

export const getValueColumnId = (attributeId: string) => {
  return `${attributeId}${valueColumnIdSuffix}`;
};

export const isValueColumnId = (columnId: string) => {
  return columnId.endsWith(valueColumnIdSuffix);
};

export const getValueColumnHeader = (title: string, attributeType: string) => {
  const config = valueColumnConfig[attributeType as AttributeFieldType];
  if (!config) {
    return undefined;
  }
  return `${title} (${translateSuffix(config.suffixKey)})`;
};

export const getValueColumn = ({
  attributeId,
  title,
  attributeType,
}: {
  attributeId: string;
  title: string;
  attributeType: AttributeFieldType;
}) => {
  const config = valueColumnConfig[attributeType];
  const header = getValueColumnHeader(title, attributeType);
  if (!config || !header) {
    return [];
  }
  return [
    {
      id: getValueColumnId(attributeId),
      title: header,
      type: 'text' as const,
      defaultHidden: true,
      afterColumnId: attributeId,
      sortable: config.isSortable ?? true,
    },
  ];
};

export const getValueColumnText = (attributeType: string, value: any) => {
  return valueColumnConfig[attributeType as AttributeFieldType]?.getText(value) ?? '';
};

const nodeLocale = {
  en: {
    descriptionBefore: 'Trigger {daysBefore} days before {referenceDate} at {time}',
    descriptionAfter: 'Trigger {daysAfter} days after {referenceDate} at {time}',
    descriptionExact: 'Trigger on {referenceDate} at {time}',
    descriptionFixed: 'Trigger on • {date} at {time}',
    configureAction: 'Configure action',
    description: 'Trigger next action after {timeLength}',
  },
};

export const getShortIdToDescriptiveMap = (actions: JourneyAction[]) => {
  const typeCounter = {} as Record<JourneyAction['action'], number>;
  const descriptiveMap = {} as Record<JourneyAction['shortId'], string>;
  actions.forEach((action) => {
    if (!typeCounter[action.action]) {
      typeCounter[action.action] = 1;
    } else {
      typeCounter[action.action] += 1;
    }
    descriptiveMap[action.shortId] = `${action.action}-${typeCounter[action.action]}`;
  });

  return descriptiveMap;
};

export const getMultiOptionValuesArray = (values: any[]): SelectOption['value'][] => {
  return values.filter((opt: SelectOption) => 'value' in opt).map((opt: SelectOption) => opt.value);
};

export const appendAttributeValues = ({
  formData,
  data,
  attributes,
  prefix,
  filesToRemove = [],
}: {
  formData: FormData;
  data: Record<string, unknown>;
  attributes: Array<AttributeType | TravellerAttributeTemplate>;
  prefix: string;
  filesToRemove?: string[];
}): void => {
  const keys = getKeys(data);

  for (const key of keys) {
    if (key.startsWith(prefix)) {
      const value = data[key] ?? '';
      const attributeKey = key.replace(prefix, '');
      const attribute = attributes.find((attr) => attr.shortId === attributeKey);
      if (attribute?.attributeType === 'date' && value instanceof Date) {
        formData.append(key, value ? format(new Date(value), 'yyyy-MM-dd') : '');
      } else if (attribute?.attributeType === 'multi_select') {
        if (!Array.isArray(value)) {
          continue;
        }
        const arrayOfValues = getMultiOptionValuesArray(value);
        formData.append(key, JSON.stringify(arrayOfValues));
      } else if (attribute?.attributeType === 'file') {
        if (value instanceof File) {
          formData.append(key, value);
        } else if (!value) {
          formData.append(`${key}__remove`, '');
        }
      } else if (attribute?.attributeType === 'file_multi') {
        if (Array.isArray(value)) {
          value.forEach((file, index) => {
            if (file instanceof File) {
              formData.append(`${key}__${index}`, file);
            }
          });
        }
        for (const fileShortId of filesToRemove) {
          formData.append(`${key}__remove__${fileShortId}`, fileShortId);
        }
      } else {
        formData.append(key, value as string);
      }
    }
  }
};

export const useCurrentWorkspace = () => {
  const params = useMatch('/workspaces/:workspaceId/*')?.params;
  const workspaceId = params?.workspaceId ?? '';
  const workspaces = useCachedWorkspaces();
  const currentWorkspace =
    workspaces.find((workspace) => workspace.shortId === workspaceId) ??
    ({
      workspaceId,
      shortId: workspaceId,
      title: '',
      description: '',
      icon: 'IconWorkspace',
      color: 'green100',
      dateCreated: '',
      dateUpdated: '',
      isPrivate: false,
      members: [],
    } as Workspace);
  return { workspaceId, ...currentWorkspace };
};

export const useWorkspaceMap = () => {
  const workspaces = useCachedWorkspaces();
  return workspaces.reduce((acc, workspace) => {
    acc[workspace.shortId] = workspace;
    return acc;
  }, {} as Record<string, Workspace>);
};
